import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef } from 'react';
import core from '@bravissimolabs/website-core';
import { each, pick } from 'lodash';
import { connect, useDispatch } from 'react-redux';
import { defineMessages, injectIntl } from 'react-intl';

import { renderRoutes } from '@bravissimolabs/react-router-config';
import Meta from 'shared/containers/MetaContainer';
import ProductPage from 'brastrap/promotional/product-page/ProductPage';
import {
  setProduct,
  selectStyleColour,
  apiGetProduct,
} from '../../../actions/product';
import { getEditorialContent, setContent } from '../../../actions/editorial';
import {
  getStyleColourCodeFromParams,
  getSlugFromUrl,
} from '../actions/product';
import {
  apiFetchReviews,
  filter,
  receiveReviews,
} from '../../../actions/product-reviews';
import { dataLayerSetProduct } from '../../../utils/data-layer';

import ProductDetails from './ProductDetailsContainer';
import ProductGallery from './ProductGalleryContainer';
import ProductReviews from './ProductReviewsContainer';
import productGroupSchemaGenerator from '../productGroupSchemaGenerator';

export const SIZE_GUIDE_SLUG = 'briefs-size-guide';

const messages = defineMessages({
  title: {
    id: 'product-page-title',
    defaultMessage: '{styleName} by {brand} | {colour} | {category}',
  },
});

const availabilityStatus = [
  'SoldOut',
  'PreOrder',
  'LimitedAvailability',
  'InStock',
];

const reviewsParams = ['page', 'limit', 'sortBy', 'starRating'];

const getCodeFromHash = hash =>
  (hash || '').replace(/.*-([A-Za-z0-9]*)$/, '$1').toUpperCase();

export const getStockStatus = status => {
  const preOrder = /^Available/gi.test(status);
  const inStock = /^In stock/gi.test(status);
  const lowStock = /^Low stock/gi.test(status);

  switch (true) {
    case preOrder:
      return 'PreOrder';
    case inStock:
      return 'InStock';
    case lowStock:
      return 'LimitedAvailability';
    default:
      return 'SoldOut';
  }
};

export const getAvailabilityStatus = (
  variants,
  offerAvailability = 'SoldOut'
) => {
  let currentOfferAvailability = offerAvailability;
  each(variants, variant => {
    if (variant.sizes) {
      currentOfferAvailability = getAvailabilityStatus(
        variant.sizes.variants,
        currentOfferAvailability
      );
    } else {
      const stockStatus = getStockStatus(variant.stockStatus);
      currentOfferAvailability =
        availabilityStatus.indexOf(stockStatus) >
        availabilityStatus.indexOf(currentOfferAvailability)
          ? stockStatus
          : currentOfferAvailability;
    }
    return currentOfferAvailability !== 'InStock';
  });
  return currentOfferAvailability;
};

const isUrlValid = (availableColours = [], code) =>
  !!availableColours.find(({ styleColourCode }) => styleColourCode === code);

const ProductPageWrapper = props => {
  const dispatch = useDispatch();

  const [featureImageHeight, setFeatureImageHeight] = useState(null);

  const productRef = useRef(null);
  const hasFetchedProductRef = useRef(false);

  useEffect(() => {
    const { styleColourCode } = props.product;
    const code =
      props.hash !== '' ? getCodeFromHash(props.hash) : styleColourCode;

    if (
      Object.keys(props.product).length !== 0 &&
      props.product.availableColours &&
      props.product.availableColours.length &&
      !isUrlValid(props.product.availableColours, code)
    ) {
      props.history.replace(props.history.location.pathname);
    }

    dispatch(selectStyleColour(code));

    if (props.slug && props.shouldFetchData) {
      dispatch(apiGetProduct(code));
      dispatch(apiFetchReviews(props.slug));
      dispatch(getEditorialContent([SIZE_GUIDE_SLUG]));
      hasFetchedProductRef.current = true;
    } else if (
      productRef.current !== props.product &&
      !hasFetchedProductRef.current
    ) {
      dataLayerSetProduct(props.product);
      productRef.current = props.product;
    }

    const unlisten = props.history.listen(() => {
      // Change selected style colour on route changes
      dispatch(
        selectStyleColour(
          props.hash ? getCodeFromHash(props.hash) : styleColourCode
        )
      );
    });

    // below will be called on unmount
    return unlisten;
  });

  const page = props.productReviews && props.productReviews.page;
  const { productRecommendations = {} } = props.product;
  const productSchema = productGroupSchemaGenerator(props);
  return (
    <Meta
      title={page > 1 ? `${props.pageTitle} | Page ${page}` : props.pageTitle}
      description={props.pageDescription || props.productDescription}
      pageType="product"
      doNotIndex={props.doNotIndex}
      schemas={[productSchema]}
    >
      {renderRoutes(props.route.routes)}
      {props.children || (
        <div>
          <ProductPage
            gallery={
              <ProductGallery
                onImageResize={height => setFeatureImageHeight(height)}
              />
            }
            details={
              <ProductDetails
                showSkeleton={
                  props.isFetchingData || props.productStateNeedsRefresh
                }
                featureImageHeight={featureImageHeight}
              />
            }
            showSkeleton={
              props.isFetchingData || props.productStateNeedsRefresh
            }
            reviews={<ProductReviews />}
            isMobile={props.isMobile}
            productRecommendations={productRecommendations}
            currency={props.currency}
            locale={props.locale}
            styleCode={props.product.styleCode}
          />
        </div>
      )}
    </Meta>
  );
};

ProductPageWrapper.populateStore = async (store, props, req, res) => {
  const styleCode = getStyleColourCodeFromParams(props.params);

  const product = await core.product.product.getProduct({ styleCode });
  // If we don't have a product throw a 404
  if (Object.keys(product).length === 0) {
    throw new Error('404');
  }

  if (product.isAccessory) {
    // Redirect the user to the home page if the product is an Accessory
    res.status(301);
    res.locals.redirectUrl = '';
  }
  await store
    .dispatch(setProduct(product))
    .then(store.dispatch(selectStyleColour(product.styleColourCode)));

  // Check parameters in querystring, and filter them for reviews
  const query = req.query;
  const reviewsFilter = pick(query, reviewsParams);

  if (reviewsFilter.page) reviewsFilter.page = parseInt(reviewsFilter.page, 10);
  reviewsFilter.limit = reviewsFilter.limit
    ? parseInt(reviewsFilter.limit, 10)
    : 8;

  const id =
    !reviewsFilter.sortBy ||
    (reviewsFilter.sortBy && reviewsFilter.sortBy === 'all')
      ? product.styleCode
      : reviewsFilter.sortBy;

  const reviews = await core.product.reviews.get(id, reviewsFilter);
  store.dispatch(receiveReviews(reviews));
  store.dispatch(filter({ ...reviewsFilter }, false));

  const sizeGuide = await core.content.editorial.getEditorialContent(
    SIZE_GUIDE_SLUG
  );
  const sizeGuideContent = {
    [SIZE_GUIDE_SLUG]:
      sizeGuide && sizeGuide.containers ? sizeGuide.containers : [],
  };
  store.dispatch(setContent(sizeGuideContent));

  // After upgrading to React Router 4, URLs that shouldn't exist have made their way into Google's Search Console
  // See ticket #2082 in Gitlab.
  const redirect =
    // If the url is not an exact match and we are not on the write review page
    // ie the invalid url: /products/au01bl/page=31/?page=3
    (!props.isExact && req.url.indexOf('write-review') === -1) ||
    // If the product has no reviews to display and we are not on page 1
    // This will ensure URLs with really high page numbers will not be indexed in Google.
    (reviewsFilter.page > 1 && reviews.length === 0);
  if (redirect) {
    // Redirect users to the first page.
    res.locals.redirectUrl = product.url.replace(/^\//, '');
    res.locals.maxAge = 604800; // 7 days
    res.status(301);
  }
};

ProductPageWrapper.propTypes = {
  children: PropTypes.node,
  doNotIndex: PropTypes.bool,
  pageTitle: PropTypes.string,
  product: PropTypes.object,
  pageDescription: PropTypes.string,
  productDescription: PropTypes.string,
  history: PropTypes.object,
  isFetchingData: PropTypes.bool,
  shouldFetchData: PropTypes.bool,
  slug: PropTypes.string,
  route: PropTypes.shape({
    routes: PropTypes.array,
  }),
  productStateNeedsRefresh: PropTypes.bool,
  productReviews: PropTypes.shape({
    limit: PropTypes.number,
    loading: PropTypes.bool,
    page: PropTypes.number,
    reviews: PropTypes.array,
    sortBy: PropTypes.string,
  }),
  hash: PropTypes.string,
  isMobile: PropTypes.bool,
  currency: PropTypes.string,
  locale: PropTypes.string,
};

ProductPageWrapper.defaultProps = {
  selectStyleColour: () => {},
};

const mapStateToProps = (state, ownProps) => {
  const {
    intl: { formatMessage },
  } = ownProps;
  const { data = {} } = state.product.styleColour;
  const {
    app: {
      config: { basePath },
      routing: { hash },
      serverAndPort,
    },
  } = state;
  const baseUrl = `${serverAndPort}${basePath}`;

  const styleColourCode = hash && getCodeFromHash(hash);
  // If the product data we have in state does not correspond with the style colour code in the hash, then
  // we know that the state needs to be refreshed
  const productStateNeedsRefresh = styleColourCode
    ? !!(
        Object.keys(data).length && !(styleColourCode === data.styleColourCode)
      )
    : false;

  // If the product needs to be refreshed, then set 'data.styleCode' to an empty string. This will ensure
  // isFetchingData is set to true and the old PDP isn't temporarily shown to user before the new one is loaded
  if (productStateNeedsRefresh) {
    data.styleCode = '';
  }

  let seoPageTitle = 'Bravissimo';

  if (Object.keys(data).length) {
    const { pageTitle, styleName, brand, colour, productGroup } = data;
    seoPageTitle =
      pageTitle ||
      formatMessage(messages.title, {
        styleName,
        brand,
        colour: colour.colour,
        category: productGroup,
      });
  }

  return {
    isFetchingData: !data.styleCode,
    shouldFetchData:
      !state.product.isFetching &&
      (!Object.keys(data).length || productStateNeedsRefresh),
    productStateNeedsRefresh,
    currency: state.app.config.currency,
    doNotIndex: data.doNotIndex,
    pageTitle: seoPageTitle,
    pageDescription: data.pageDescription,
    product: data,
    productDescription: data.description,
    productReviews: state.productReviews,
    slug: getSlugFromUrl(state.app.routing.url),
    baseUrl,
    hash,
    isMobile: state.browser.deviceIs.mobile,
    locale: state.app.locale.selected,
  };
};

export default injectIntl(connect(mapStateToProps)(ProductPageWrapper));
